import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, Box, Typography, TextField, LinearProgress, Grid } from '@mui/material';

import { fetchInvoicingReport } from '../../../services/api';
import InvoicingRevenueChart from './InvoicingRevenueChart';
import ServiceBreakdown from './ServiceBreakdown';
import CustomerBreakdown from './CustomerBreakdown';

const pct = (value, total) => total > 0 ? Math.round((value / total) * 100) : 0;

const formatCurrency = (value, currency = 'EUR') => (
    new Intl.NumberFormat('fr-BE', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
    }).format(value || 0)
);

const InvoicingDashboard = () => {
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const loadReport = useCallback(async (targetYear) => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetchInvoicingReport({ year: targetYear });
            if (response?.success) {
                setReport(response);
            } else {
                setError(response?.message || 'Erreur de chargement du rapport');
            }
        } catch (e) {
            setError(e.message || 'Erreur de chargement du rapport');
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadReport(year);
    }, [year, loadReport]);

    const handleYearChange = (event) => {
        const val = Number(event.target.value);
        if (!Number.isNaN(val)) {
            setYear(val);
        }
    };

    return (
        <Box mt={2}>
            <Card>
                <CardContent>
                    <Box display="flex" alignItems="center" mb={2} gap={2}>
                        <Typography variant="h6">Chiffre d'affaires</Typography>
                        <Box flexGrow={1} />
                        <TextField
                            label="Annee"
                            type="number"
                            size="small"
                            value={year}
                            onChange={handleYearChange}
                            sx={{ width: 120 }}
                        />
                    </Box>

                    {loading && <LinearProgress />}

                    {error && (
                        <Typography color="error" variant="body2" mb={2}>
                            {error}
                        </Typography>
                    )}

                    {report && !loading && (
                        <>
                            {/* Key Metrics */}
                            <Box display="flex" flexWrap="wrap" gap={3} mb={4}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        CA HT
                                    </Typography>
                                    <Typography variant="h5">
                                        {formatCurrency(report.totals?.revenue_untaxed)}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        TVA
                                    </Typography>
                                    <Typography variant="h5">
                                        {formatCurrency(report.totals?.revenue_vat)}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        CA TTC
                                    </Typography>
                                    <Typography variant="h5">
                                        {formatCurrency(report.totals?.revenue_total)}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Factures
                                    </Typography>
                                    <Typography variant="h5">
                                        {report.totals?.invoice_count || 0}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Projections */}
                            {report.projections && (
                                <Box sx={{ mb: 4, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'action.hover' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                        Projections de Chiffre d'Affaires
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={3}>
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                CA Réel
                                            </Typography>
                                            <Typography variant="h5">
                                                {formatCurrency(report.projections.actual_invoiced)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Budgets Restants
                                            </Typography>
                                            <Typography variant="h5">
                                                {formatCurrency(report.projections.projected_budget)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Leads Qualifiés (>50°)
                                            </Typography>
                                            <Typography variant="h5">
                                                {formatCurrency(report.projections.projected_leads)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                Total Projeté
                                            </Typography>
                                            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                                {formatCurrency(report.projections.total_projected)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {report.projections.leads_data && report.projections.leads_data.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                Détail des leads ({report.projections.leads_data.length})
                                            </Typography>
                                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {report.projections.leads_data.map((lead) => (
                                                    <Box key={lead.id} sx={{ p: 1, backgroundColor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', fontSize: 'small' }}>
                                                        <Typography variant="caption" display="block">{lead.full_name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{lead.temperature}° - {(lead.probability * 100).toFixed(0)}%</Typography>
                                                        <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>{formatCurrency(lead.expected_amount)}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* KPIs and Objectives */}
                            {report.kpis && report.objectives && (
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                        Position Actuelle et Objectifs
                                    </Typography>

                                    {/* Current Position Card */}
                                    <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'action.hover' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Position actuelle:
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                                                {formatCurrency(report.kpis.actual_revenue)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">+</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                                                {formatCurrency(report.kpis.projected_budgets)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">+</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#38bdf8' }}>
                                                {formatCurrency(report.projections.projected_leads)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">=</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                                {formatCurrency(report.projections.total_projected)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Progression de l'année: {Math.round(report.kpis.progress_ratio * 100)}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={report.kpis.progress_ratio * 100}
                                            sx={{ height: 6, borderRadius: 1 }}
                                        />
                                    </Box>

                                    {/* Objective Targets */}
                                    <Grid container spacing={2}>
                                        {/* Worst Case */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                    Pire Cas
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                                    {formatCurrency(report.objectives.worst_case)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Attendu:
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                        {formatCurrency(report.kpis.expected_worst)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Facturé:
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                        {formatCurrency(report.kpis.actual_revenue)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: pct(report.kpis.actual_revenue, report.kpis.expected_worst) < 50 ? 'error.main' : 'text.secondary' }}>
                                                        {pct(report.kpis.actual_revenue, report.kpis.expected_worst)}% rempli
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ height: 20, backgroundColor: 'action.disabled', borderRadius: 1, overflow: 'hidden', display: 'flex' }}>
                                                            <Box
                                                                sx={{
                                                                    height: '100%',
                                                                    width: `${Math.min(pct(report.kpis.actual_revenue, report.objectives.worst_case), 100)}%`,
                                                                    backgroundColor: 'success.main',
                                                                }}
                                                            />
                                                            <Box
                                                                sx={{
                                                                    height: '100%',
                                                                    width: `${Math.min(pct(report.kpis.projected_budgets, report.objectives.worst_case), 100)}%`,
                                                                    backgroundColor: 'warning.main',
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Box sx={{ width: 10, height: 10, backgroundColor: 'success.main', borderRadius: '2px' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatCurrency(report.kpis.actual_revenue)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Box sx={{ width: 10, height: 10, backgroundColor: 'warning.main', borderRadius: '2px' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatCurrency(report.kpis.projected_budgets)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Middle Case */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                    Cas Moyen
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                                    {formatCurrency(report.objectives.middle_case)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Attendu:
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                        {formatCurrency(report.kpis.expected_middle)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Facturé:
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                        {formatCurrency(report.kpis.actual_revenue)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: pct(report.kpis.actual_revenue, report.kpis.expected_middle) < 50 ? 'error.main' : 'text.secondary' }}>
                                                        {pct(report.kpis.actual_revenue, report.kpis.expected_middle)}% rempli
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ height: 20, backgroundColor: 'action.disabled', borderRadius: 1, overflow: 'hidden', display: 'flex' }}>
                                                            <Box
                                                                sx={{
                                                                    height: '100%',
                                                                    width: `${Math.min(pct(report.kpis.actual_revenue, report.objectives.middle_case), 100)}%`,
                                                                    backgroundColor: 'success.main',
                                                                }}
                                                            />
                                                            <Box
                                                                sx={{
                                                                    height: '100%',
                                                                    width: `${Math.min(pct(report.kpis.projected_budgets, report.objectives.middle_case), 100)}%`,
                                                                    backgroundColor: 'warning.main',
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Box sx={{ width: 10, height: 10, backgroundColor: 'success.main', borderRadius: '2px' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatCurrency(report.kpis.actual_revenue)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Box sx={{ width: 10, height: 10, backgroundColor: 'warning.main', borderRadius: '2px' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatCurrency(report.kpis.projected_budgets)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Best Case */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                    Meilleur Cas
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                                    {formatCurrency(report.objectives.best_case)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Attendu:
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                        {formatCurrency(report.kpis.expected_best)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Facturé:
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                        {formatCurrency(report.kpis.actual_revenue)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: pct(report.kpis.actual_revenue, report.kpis.expected_best) < 50 ? 'error.main' : 'text.secondary' }}>
                                                        {pct(report.kpis.actual_revenue, report.kpis.expected_best)}% rempli
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ height: 20, backgroundColor: 'action.disabled', borderRadius: 1, overflow: 'hidden', display: 'flex' }}>
                                                            <Box
                                                                sx={{
                                                                    height: '100%',
                                                                    width: `${Math.min(pct(report.kpis.actual_revenue, report.objectives.best_case), 100)}%`,
                                                                    backgroundColor: 'success.main',
                                                                }}
                                                            />
                                                            <Box
                                                                sx={{
                                                                    height: '100%',
                                                                    width: `${Math.min(pct(report.kpis.projected_budgets, report.objectives.best_case), 100)}%`,
                                                                    backgroundColor: 'warning.main',
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Box sx={{ width: 10, height: 10, backgroundColor: 'success.main', borderRadius: '2px' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatCurrency(report.kpis.actual_revenue)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Box sx={{ width: 10, height: 10, backgroundColor: 'warning.main', borderRadius: '2px' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatCurrency(report.kpis.projected_budgets)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            <InvoicingRevenueChart data={report.data} freq="monthly" />

                            <Box display="flex" flexWrap="wrap" gap={4} sx={{ mt: 4 }}>
                                <Box sx={{ flex: 1, minWidth: 320 }}>
                                    <ServiceBreakdown services={report.services} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 320 }}>
                                    <CustomerBreakdown customers={report.customers} />
                                </Box>
                            </Box>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default InvoicingDashboard;
